/**
 * AppFactory Device Types Context
 * 使用與 Device Types 頁面相同的 Firestore 資料來源
 * slot1 icon 優先使用 Firestore 上傳的自訂 SVG
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { DeviceTypeRegistryEntry } from '../services/deviceTypeRegistryStore'
import { listDeviceTypeEntriesFromDeviceTypes, listDeviceTypesMeta } from '../services/deviceTypesStore'

const DEVICE_ICON_FALLBACK: Record<string, string> = {
  light: 'lightbulb',
  hvac: 'ac_unit',
  ac_unit: 'ac_unit',
  fan: 'mode_fan',
  switch: 'toggle_on',
  outlet: 'outlet',
  th_sensor: 'device_thermostat',
  presence_sensor: 'motion_sensor_active',
  lux_sensor: 'light_mode',
  multi_sensor: 'sensors',
  power_meter: 'electric_meter',
  power_meter_3phase: 'bolt',
  electrical_sensor_1p3w: 'electric_meter',
  electrical_sensor_3p4w: 'bolt',
  door_sensor: 'sensor_door',
  contact_sensor: 'sensor_door',
  button: 'touch_app',
  dehumidifier: 'humidity_mid',
  erv: 'air',
  curtain: 'blinds',
  blind: 'blinds',
  blinds: 'blinds',
  generic: 'devices',
}

export type IconSlot1 = {
  dataUrl?: string
  materialIcon: string
}

type AppFactoryDeviceTypesContextValue = {
  registryEntries: Array<{ deviceType: string }>
  registryEntryMap: Record<string, DeviceTypeRegistryEntry>
  loading: boolean
  error: string | null
  selectedType: string
  setSelectedType: (v: string) => void
  getIconSlot1: (deviceType: string) => IconSlot1
}

const AppFactoryDeviceTypesContext = createContext<AppFactoryDeviceTypesContextValue | null>(null)

function toDeviceTypeKey(value: unknown): string {
  return String(value || '').trim()
}

export function AppFactoryDeviceTypesProvider({ children }: { children: ReactNode }) {
  const [registryEntries, setRegistryEntries] = useState<Array<{ deviceType: string }>>([])
  const [registryEntryMap, setRegistryEntryMap] = useState<Record<string, DeviceTypeRegistryEntry>>({})
  const [deviceTypeMetaMap, setDeviceTypeMetaMap] = useState<Record<string, { slot1DataUrl?: string }>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState<string>('')

  useEffect(() => {
    Promise.all([listDeviceTypesMeta().catch(() => [])])
      .then(([metaList]) => {
        return Promise.all([
          Promise.resolve(metaList),
          listDeviceTypeEntriesFromDeviceTypes(metaList).catch(() => [] as DeviceTypeRegistryEntry[]),
        ])
      })
      .then(([metaList, entries]) => {
        const entryMap: Record<string, DeviceTypeRegistryEntry> = {}
        for (const entry of entries) {
          const deviceType = toDeviceTypeKey(entry?.deviceType)
          if (!deviceType) continue
          entryMap[deviceType] = entry
        }
        const meta: Record<string, { slot1DataUrl?: string }> = {}
        for (const m of metaList || []) {
          const deviceType = toDeviceTypeKey(m?.deviceType)
          if (!deviceType) continue
          const slot1 = m.iconSlots?.find((s) => s.id === 'slot1')
          const dataUrl = slot1?.dataUrl?.trim()
          if (dataUrl) meta[deviceType] = { slot1DataUrl: dataUrl }
        }

        const allTypes = Object.keys(entryMap).sort((a, b) => a.localeCompare(b))
        setRegistryEntries(allTypes.map((deviceType) => ({ deviceType })))
        setRegistryEntryMap(entryMap)
        setDeviceTypeMetaMap(meta)
        if (allTypes.length > 0) {
          setSelectedType((prev) => prev || allTypes[0])
        }
      })
      .catch((e) => setError(e?.message ?? '載入失敗'))
      .finally(() => setLoading(false))
  }, [])

  const setSelected = useCallback((v: string) => setSelectedType(v), [])

  const getIconSlot1 = useCallback(
    (deviceType: string): IconSlot1 => {
      const meta = deviceTypeMetaMap[deviceType]
      const dataUrl = meta?.slot1DataUrl
      const key = deviceType?.toLowerCase() || ''
      const materialIcon = DEVICE_ICON_FALLBACK[key] ?? DEVICE_ICON_FALLBACK.generic ?? 'devices'
      return { dataUrl, materialIcon }
    },
    [deviceTypeMetaMap]
  )

  const value: AppFactoryDeviceTypesContextValue = {
    registryEntries,
    registryEntryMap,
    loading,
    error,
    selectedType,
    setSelectedType: setSelected,
    getIconSlot1,
  }

  return (
    <AppFactoryDeviceTypesContext.Provider value={value}>
      {children}
    </AppFactoryDeviceTypesContext.Provider>
  )
}

export function useAppFactoryDeviceTypes() {
  const ctx = useContext(AppFactoryDeviceTypesContext)
  if (!ctx) {
    throw new Error('useAppFactoryDeviceTypes must be used within AppFactoryDeviceTypesProvider')
  }
  return ctx
}

export function useAppFactoryDeviceTypesOptional() {
  return useContext(AppFactoryDeviceTypesContext)
}
